import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ClassContext } from '../context/ClassContext';
import api from '../api';
import ScheduleModal from './ScheduleModal';

const Calendar = () => {
    const { classes, getClasses } = useContext(ClassContext);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - dayOfWeek);
        return startOfWeek;
    });
    const [currentDay, setCurrentDay] = useState(new Date());
    const [isMobile, setIsMobile] = useState(false);
    const [scheduleEntries, setScheduleEntries] = useState([]);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Ensure arrays are always arrays to prevent TypeError
    const scheduleEntriesArray = useMemo(() =>
        Array.isArray(scheduleEntries) ? scheduleEntries : [], [scheduleEntries]);
    const classesArray = useMemo(() =>
        Array.isArray(classes) ? classes : [], [classes]);
    
    const monthNames = [
        'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
        'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
    ];
    
    const dayLabels = useMemo(() => ({
        'monday': 'Thứ Hai',
        'tuesday': 'Thứ Ba',
        'wednesday': 'Thứ Tư',
        'thursday': 'Thứ Năm',
        'friday': 'Thứ Sáu',
        'saturday': 'Thứ Bảy',
        'sunday': 'Chủ Nhật'
    }), []);

    useEffect(() => {
        getClasses();
    }, [getClasses]);

    // Fetch schedule entries for the current week
    useEffect(() => {
        const fetchScheduleEntries = async () => {
            try {
                const endOfWeek = new Date(currentWeekStart);
                endOfWeek.setDate(currentWeekStart.getDate() + 6);

                const startDateStr = currentWeekStart.toISOString().split('T')[0];
                const endDateStr = endOfWeek.toISOString().split('T')[0];

                const res = await api.get(`/schedules/range?startDate=${startDateStr}&endDate=${endDateStr}`);
                setScheduleEntries(res.data);
            } catch (error) {
                console.error('Error fetching schedule entries:', error);
                setScheduleEntries([]);
            }
        };

        fetchScheduleEntries();
    }, [currentWeekStart]);

    // Fetch schedule entries for the current day (mobile view)
    useEffect(() => {
        const fetchDayScheduleEntries = async () => {
            try {
                const dateStr = currentDay.toISOString().split('T')[0];
                const res = await api.get(`/schedules/date/${dateStr}`);
                setScheduleEntries(res.data);
            } catch (error) {
                console.error('Error fetching day schedule entries:', error);
                setScheduleEntries([]);
            }
        };

        if (isMobile) {
            fetchDayScheduleEntries();
        }
    }, [currentDay, isMobile]);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const getSchedulesForDay = useCallback((dayOfWeek, specificDate = null) => {
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[dayOfWeek];
        const daySchedules = [];

        // First, check for specific schedule entries with descriptions
        if (specificDate && scheduleEntriesArray.length > 0) {
            const dateStr = specificDate.toISOString().split('T')[0];
            const dateEntries = scheduleEntriesArray.filter(entry => {
                const entryDate = new Date(entry.date).toISOString().split('T')[0];
                return entryDate === dateStr;
            });

            dateEntries.forEach(entry => {
                daySchedules.push({
                    startTime: entry.startTime,
                    endTime: entry.endTime,
                    className: entry.classId.name,
                    classId: entry.classId._id,
                    title: entry.title,
                    description: entry.description,
                    status: entry.status,
                    isDetailedEntry: true
                });
            });
        }

        // If no specific entries found, fall back to class schedules
        if (daySchedules.length === 0) {
            classesArray.forEach(classItem => {
                const schedulesArray = Array.isArray(classItem.schedules) ? classItem.schedules : [];
                if (schedulesArray.length > 0) {
                    schedulesArray.forEach(schedule => {
                        if (schedule.dayOfWeek === dayName) {
                            daySchedules.push({
                                ...schedule,
                                className: classItem.name,
                                classId: classItem._id,
                                isDetailedEntry: false
                            });
                        }
                    });
                }
            });
        }

        return daySchedules.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }, [classesArray, scheduleEntriesArray]);

    const getCurrentDaySchedule = useCallback(() => {
        const dayOfWeek = currentDay.getDay();
        return getSchedulesForDay(dayOfWeek, currentDay);
    }, [currentDay, getSchedulesForDay]);

    const generateWeek = useCallback(() => {
        const week = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(currentWeekStart.getDate() + i);
            const dayOfWeek = date.getDay();
            const schedules = getSchedulesForDay(dayOfWeek, date);

            week.push({
                date: date.getDate(),
                fullDate: date,
                dayOfWeek,
                schedules
            });
        }
        return week;
    }, [currentWeekStart, getSchedulesForDay]);

    const weeklySchedules = useMemo(() => {
        return Object.entries(dayLabels).map(([dayKey, dayLabel]) => {
            const daySchedules = [];

            // Get schedules for each day of the week from the current week
            const weekDays = generateWeek();
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const dayIndex = dayNames.indexOf(dayKey);

            if (dayIndex !== -1) {
                const weekDay = weekDays.find(day => day.dayOfWeek === dayIndex);
                if (weekDay && weekDay.schedules) {
                    daySchedules.push(...weekDay.schedules);
                }
            }

            return {
                dayKey,
                dayLabel,
                schedules: daySchedules.sort((a, b) => a.startTime.localeCompare(b.startTime))
            };
        }).filter(({ schedules }) => schedules.length > 0);
    }, [dayLabels, generateWeek]);


    const navigateWeek = (direction) => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(currentWeekStart.getDate() + (direction * 7));
        setCurrentWeekStart(newDate);
    };

    const navigateDay = (direction) => {
        const newDate = new Date(currentDay);
        newDate.setDate(currentDay.getDate() + direction);
        setCurrentDay(newDate);
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentDay(today);

        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - dayOfWeek);
        setCurrentWeekStart(startOfWeek);
    };

    const formatTime = (time) => time.substring(0, 5);

    const isToday = (date) => {
        const today = new Date();
        return date &&
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const formatDayDate = (date) => {
        const dayNames = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
        return `${dayNames[date.getDay()]}, ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    };

    const getWeekDateRange = () => {
        const endOfWeek = new Date(currentWeekStart);
        endOfWeek.setDate(currentWeekStart.getDate() + 6);
        
        const startMonth = currentWeekStart.toLocaleDateString('vi-VN', { month: 'long' });
        const endMonth = endOfWeek.toLocaleDateString('vi-VN', { month: 'long' });
        
        if (currentWeekStart.getMonth() === endOfWeek.getMonth()) {
            return `${currentWeekStart.getDate()} - ${endOfWeek.getDate()} ${startMonth} ${currentWeekStart.getFullYear()}`;
        } else {
            return `${currentWeekStart.getDate()} ${startMonth} - ${endOfWeek.getDate()} ${endMonth} ${currentWeekStart.getFullYear()}`;
        }
    };

    const weekDays = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

    const openScheduleModal = (schedule, date = null) => {
        const scheduleWithDate = { ...schedule };
        if (date) {
            scheduleWithDate.date = date;
        }
        setSelectedSchedule(scheduleWithDate);
        setIsModalOpen(true);
    };

    const closeScheduleModal = () => {
        setIsModalOpen(false);
        setSelectedSchedule(null);
    };

    return (
        <div className="space-y-4 rounded-3xl bg-white/90 p-4 shadow-xl md:space-y-8 md:p-8">
            {/* Mobile Daily View */}
            {isMobile ? (
                <>
                    {/* Mobile Header */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <button
                                className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-xl text-indigo-600 transition-all duration-200 hover:bg-indigo-200 active:scale-95"
                                onClick={() => navigateDay(-1)}
                            >
                                ‹
                            </button>
                            <div className="flex-1 text-center">
                                <h2 className="text-lg font-bold text-slate-900">
                                    {formatDayDate(currentDay)}
                                </h2>
                                <p className="text-sm text-slate-500">Lịch học hôm nay</p>
                            </div>
                            <button
                                className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-xl text-indigo-600 transition-all duration-200 hover:bg-indigo-200 active:scale-95"
                                onClick={() => navigateDay(1)}
                            >
                                ›
                            </button>
                        </div>
                        <button
                            className="w-full rounded-2xl bg-indigo-600 py-3 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:bg-indigo-700 active:scale-95"
                            onClick={goToToday}
                        >
                            📅 Về hôm nay
                        </button>
                    </div>

                    {/* Mobile Daily Schedule */}
                    <div className="space-y-4 rounded-2xl bg-gradient-to-br from-slate-50 to-indigo-50/30 p-4 shadow-inner">
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full bg-indigo-500"></div>
                            <h3 className="text-base font-semibold text-slate-900">
                                Lịch học hôm nay
                            </h3>
                        </div>
                        
                        <div className="space-y-3">
                            {getCurrentDaySchedule().length > 0 ? (
                                getCurrentDaySchedule().map((schedule, idx) => (
                                    <div
                                        key={idx}
                                        className="group rounded-2xl bg-white p-4 shadow-sm border border-indigo-100/50 transition-all duration-200 hover:shadow-md active:scale-[0.98] cursor-pointer"
                                        onClick={() => openScheduleModal(schedule, currentDay)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                                                        <span className="text-sm font-bold text-indigo-600">📚</span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <h4 className="text-base font-semibold text-slate-900 leading-tight">
                                                            {schedule.title || schedule.className}
                                                        </h4>
                                                        {schedule.className !== schedule.title && schedule.className && (
                                                            <p className="text-xs text-slate-500 mt-1">{schedule.className}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-indigo-500">🕐</span>
                                                        <span className="font-medium">
                                                            {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                                        </span>
                                                    </div>
                                                </div>
                                                {schedule.description && (
                                                    <div className="mt-3 p-2 bg-slate-50 rounded-lg">
                                                        <p className="text-sm text-slate-700">{schedule.description}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="rounded-2xl bg-white/70 py-12 text-center">
                                    <div className="space-y-3">
                                        <div className="text-4xl">📝</div>
                                        <div className="space-y-1">
                                            <h4 className="font-medium text-slate-700">Không có lịch học</h4>
                                            <p className="text-sm text-slate-500">Hôm nay bạn không có tiết học nào</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile Quick Navigation */}
                    <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 p-4">
                        <h3 className="mb-3 text-sm font-semibold text-slate-700">⚡ Điều hướng nhanh</h3>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                className="rounded-xl bg-white/80 py-3 text-center text-xs font-medium text-slate-700 shadow-sm transition-all duration-200 hover:bg-white active:scale-95"
                                onClick={() => navigateDay(-1)}
                            >
                                ← Hôm qua
                            </button>
                            <button
                                className="rounded-xl bg-indigo-100 py-3 text-center text-xs font-medium text-indigo-700 shadow-sm transition-all duration-200 hover:bg-indigo-200 active:scale-95"
                                onClick={goToToday}
                            >
                                Hôm nay
                            </button>
                            <button
                                className="rounded-xl bg-white/80 py-3 text-center text-xs font-medium text-slate-700 shadow-sm transition-all duration-200 hover:bg-white active:scale-95"
                                onClick={() => navigateDay(1)}
                            >
                                Ngày mai →
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Desktop Header */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                            <button
                                className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-xl text-indigo-600 transition hover:bg-indigo-100"
                                onClick={() => navigateWeek(-1)}
                            >
                                ‹
                            </button>
                            <h2 className="text-2xl font-semibold text-slate-900">
                                {getWeekDateRange()}
                            </h2>
                            <button
                                className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-xl text-indigo-600 transition hover:bg-indigo-100"
                                onClick={() => navigateWeek(1)}
                            >
                                ›
                            </button>
                        </div>
                        <button
                            className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                            onClick={goToToday}
                        >
                            📅 Hôm nay
                        </button>
                    </div>

                    {/* Desktop Weekly View */}
                    <div className="space-y-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-6 shadow-inner">
                        <div className="grid grid-cols-7 gap-3 text-center text-sm font-semibold uppercase tracking-wide text-slate-500">
                            {weekDays.map(day => (
                                <div key={day} className="rounded-lg bg-white/60 px-3 py-3 shadow-sm">
                                    {day}
                                </div>
                            ))}
                        </div>

                        <div className="grid min-h-[24rem] grid-cols-7 gap-3 text-left text-sm">
                            {generateWeek().map((day, index) => (
                                <div
                                    key={index}
                                    className={`flex flex-col rounded-xl border border-dashed border-slate-200 bg-white/90 p-3 shadow-sm transition hover:border-indigo-200 hover:shadow-lg ${isToday(day.fullDate) ? 'border-indigo-400 bg-indigo-50/80 ring-2 ring-indigo-200' : ''}`}
                                >
                                    <div className="flex items-center justify-between text-sm font-semibold text-slate-500">
                                        <span className="text-base">{day.date}</span>
                                        <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs text-indigo-600">
                                            {weekDays[day.dayOfWeek]}
                                        </span>
                                    </div>
                                    <div className="mt-3 flex flex-col gap-2">
                                        {day.schedules.map((schedule, idx) => (
                                            <div
                                                key={idx}
                                                className="rounded-lg bg-indigo-100/80 px-3 py-2 text-xs text-indigo-700 transition hover:bg-indigo-200/80 cursor-pointer"
                                                title={`${schedule.title || schedule.className} - ${formatTime(schedule.startTime)} đến ${formatTime(schedule.endTime)}${schedule.description ? `\n${schedule.description}` : ''}`}
                                                onClick={() => openScheduleModal(schedule, day.fullDate)}
                                            >
                                                <div className="font-semibold">{formatTime(schedule.startTime)}</div>
                                                <div className="truncate text-[11px]">{schedule.title || schedule.className}</div>
                                                {schedule.description && (
                                                    <div className="truncate text-[10px] text-indigo-600 mt-1">
                                                        {schedule.description}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {day.schedules.length === 0 && (
                                            <div className="py-6 text-center text-xs text-slate-400">
                                                Không có lịch học
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Weekly Schedule Summary (Show on both views) */}
            <div className="space-y-4 rounded-2xl border border-slate-100 bg-white/90 p-4 shadow-lg md:p-6">
                <div className="flex items-center gap-2">
                    <span className="text-lg">📚</span>
                    <h3 className="text-base font-semibold text-slate-900 md:text-lg">Lịch học trong tuần</h3>
                </div>
                
                {isMobile ? (
                    /* Mobile Weekly Schedule - Vertical Layout */
                    <div className="space-y-3">
                        {weeklySchedules.map(({ dayKey, dayLabel, schedules }) => (
                            <div key={dayKey} className="rounded-xl bg-slate-50/80 p-3">
                                <h4 className="mb-2 text-sm font-semibold text-slate-700">{dayLabel}</h4>
                                <div className="space-y-2">
                                    {schedules.map((schedule, idx) => (
                                        <div key={idx} className="rounded-lg bg-white/90 p-3 shadow-sm cursor-pointer hover:bg-white transition-colors duration-200" onClick={() => openScheduleModal(schedule)}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-medium text-indigo-600">
                                                    {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                                </span>
                                                <span className="truncate pl-2 text-right text-xs text-slate-600">
                                                    {schedule.title || schedule.className}
                                                </span>
                                            </div>
                                            {schedule.description && (
                                                <div className="text-xs text-slate-500 mt-1">
                                                    {schedule.description}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {weeklySchedules.length === 0 && (
                            <div className="rounded-xl bg-slate-50/80 py-8 text-center">
                                <div className="space-y-2">
                                    <div className="text-2xl">�</div>
                                    <p className="text-sm text-slate-500">Chưa có lịch học nào trong tuần</p>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Desktop Weekly Schedule - Grid Layout */
                    <div className="grid gap-4 md:grid-cols-2">
                        {weeklySchedules.map(({ dayKey, dayLabel, schedules }) => (
                            <div key={dayKey} className="rounded-2xl bg-slate-50/80 p-4">
                                <h4 className="text-sm font-semibold text-slate-700">{dayLabel}</h4>
                                <div className="mt-3 space-y-2 text-xs text-slate-500">
                                    {schedules.map((schedule, idx) => (
                                        <div key={idx} className="rounded-lg bg-white/80 p-3 shadow-sm cursor-pointer hover:bg-white transition-colors duration-200" onClick={() => openScheduleModal(schedule)}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-medium text-indigo-600">
                                                    {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                                </span>
                                                <span className="truncate pl-3 text-right">
                                                    {schedule.title || schedule.className}
                                                </span>
                                            </div>
                                            {schedule.description && (
                                                <div className="text-xs text-slate-500 mt-1">
                                                    {schedule.description}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Schedule Details Modal */}
            <ScheduleModal
                isOpen={isModalOpen}
                onClose={closeScheduleModal}
                onBackdropClick={closeScheduleModal}
                schedule={selectedSchedule}
            />
        </div>
    );
};

export default Calendar;
